// src/hooks/useContainerWidth.js
import api from "../config/axiosConfig";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import * as yup from "yup";
import { convertToFormData } from "../config/convertToFormData";
import { debounce } from "lodash";

const validationSchema = yup.object({
  component_name: yup.string().required("Component name is required"),
  images: yup
    .array()
    .min(1, "At least one image is required")
    .max(15, "Maximum 15 images allowed"),
  items: yup
    .array()
    .of(
      yup.object().shape({
        item_name: yup.string().required("Item name is required"),
        sub_parts: yup
          .array()
          .of(
            yup.object().shape({
              description: yup.string().required("Description is required"),
              specification: yup.string().required("Specification is required"),
              tolerance: yup.string().required("Tolerance is required"),
            })
          )
          .min(1, "At least one subpart is required"),
      })
    )
    .min(1, "At least one item is required"),
});

// Outside your component (or inside using useCallback)
const debouncedTitleUpdate = debounce(
  async (title, editingData, handleUpdateTitleAndImages) => {
    if (editingData) {
      await handleUpdateTitleAndImages({
        component_name: title,
      });
    }
  },
  500
);

const debouncedItemNameUpdate = debounce(async (id, name, api) => {
  await api.put(`/api/rfi-components/item/${id}`, { item_name: name });
}, 500);

const debouncedSubpartUpdate = debounce(async (id, updatedSubpart, api) => {
  await api.put(`/api/rfi-components/subpart`, {
    subpart_id: id,
    ...updatedSubpart,
  });
}, 500);

export default function useCreateEditComponentContent(
  isTemplateCreation,
  onClose,
  initialData,
  onSave,
  open
) {
  const [imageFiles, setImageFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [models, setModels] = useState([]);

  const formik = useFormik({
    initialValues: {
      model_id: isTemplateCreation ? "" : undefined,
      component_name: "",
      images: [],
      items: [
        {
          item_name: "",
          sub_parts: [
            {
              description: "",
              specification: "",
              tolerance: "",
            },
          ],
        },
      ],
    },
    validationSchema: isTemplateCreation
      ? validationSchema.concat(
          yup.object({
            model_id: yup.string().required("Model is required"),
          })
        )
      : validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const { model_name, ...submitValues } = values;
        const response = await onSave(submitValues);
        if (response.success) {
          resetForm();
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleModalChange = (event, newValue) => {
    formik.setFieldValue("model_id", newValue?.model_id || "");
    // formik.setFieldTouched("model_id", true);
  };

  const resetForm = () => {
    formik.resetForm();
    setImageFiles([]);
  };

  const handleClose = () => {
    if (checkNewItems || checkNewSubparts) {
      toast.error("Please save the changes before closing the modal");
      return;
    }
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (initialData) {
      const values = {
        ...initialData,
        model_id: isTemplateCreation ? initialData.model_id || "" : undefined,
        model_name: isTemplateCreation
          ? models?.find((m) => m.model_id === initialData.model_id)
              ?.model_name || ""
          : undefined,
      };
      formik.setValues(values);

      if (initialData.component_images) {
        // Set the image URLs directly for display
        setImageFiles(initialData.component_images.map((img) => img.image_url));
        // Initialize the images array in formik with the existing images
        formik.setFieldValue("images", initialData.component_images);
      }
    } else if (!open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, open, models]);

  const handleImageUpload = async (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      if (files.length + formik.values.images.length > 15) {
        formik.setFieldError("images", "Maximum 15 images allowed");
        return;
      }
      const updatedImages = [...formik.values.images, ...files];
      // formik.setFieldValue("images", updatedImages);
      const newImagePreviews = files.map((file) => URL.createObjectURL(file));
      setImageFiles((prev) => [...prev, ...newImagePreviews]);
      if (initialData?.component_id) {
        try {
          const componentData = {
            // ...formik.values,
            images: updatedImages,
          };
          const response = await handleUpdateTitleAndImages(componentData);
          formik.setFieldValue("images", response.images);

          if (!response.success) {
            throw new Error(response.error || "Failed to update images");
          }
          if (response.data) {
            formik.setFieldValue("images", response.data.images);
          }
        } catch (err) {
          console.error("Error uploading images:", err);
          formik.setFieldError("images", "Failed to upload images");
          formik.setFieldValue(
            "images",
            formik.values.images.slice(0, -files.length)
          );
          setImageFiles((prev) => prev.slice(0, -files.length));
        }
      } else {
        formik.setFieldValue("images", updatedImages);
      }
    }
  };

  const addItem = () => {
    const newItem = {
      item_name: "",
      sub_parts: [
        {
          description: "",
          specification: "",
          tolerance: "",
        },
      ],
    };
    formik.setFieldValue("items", [...formik.values.items, newItem]);
  };

  const removeItem = (itemIndex, itemId) => {
    const newItems = [...formik.values.items];
    newItems.splice(itemIndex, 1);
    formik.setFieldValue("items", newItems);
    if (itemId) {
      handleDeleteItemAPI(itemId);
    }
  };

  const addSubPart = (itemIndex) => {
    const newItems = [...formik.values.items];
    newItems[itemIndex].sub_parts.push({
      description: "",
      specification: "",
      tolerance: "",
    });
    formik.setFieldValue("items", newItems);
  };

  const removeSubPart = (itemIndex, subPartIndex, subPartId) => {
    const newItems = [...formik.values.items];
    newItems[itemIndex].sub_parts.splice(subPartIndex, 1);
    formik.setFieldValue("items", newItems);
    if (subPartId) {
      handleDeleteSubpartAPI(subPartId);
    }
  };

  useEffect(() => {
    getModalsAPI();
  }, []);

  // API's below
  const getModalsAPI = async () => {
    try {
      const response = await api.get("/api/rfi-component-templates/model/list");
      setModels(response?.data?.data || []);
      return response.data;
    } catch (error) {
      console.error("Error fetching models:", error);
      return [];
    }
  };

  //API for updating title and images
  const handleUpdateTitleAndImages = async (componentData) => {
    const changeDataToFormData = convertToFormData(componentData);
    return await api
      .put(
        `/api/rfi-components/${initialData.component_id}`,
        changeDataToFormData
      )
      .then((response) => {
        return { success: true, ...response.data };
      })
      .catch((err) => {
        console.error("Error updating component:", err);
        return { success: false, error: err };
      });
  };

  const removeImage = async (e, index) => {
    e.stopPropagation();
    try {
      const imageToRemove = formik.values.images[index];
      const isFileObject = imageToRemove instanceof File;
      const newImages = [...formik.values.images];
      newImages.splice(index, 1);
      formik.setFieldValue("images", newImages);

      const newImageFiles = [...imageFiles];
      newImageFiles.splice(index, 1);
      setImageFiles(newImageFiles);

      if (
        initialData?.component_id &&
        !isFileObject &&
        imageToRemove.image_id
      ) {
        await api.delete(
          `/api/rfi-components/images/${imageToRemove.image_id}`
        );
        toast.success("Image deleted successfully");
      }
    } catch (err) {
      console.error("Error deleting image:", err);
      const originalImages = [...formik.values.images];
      formik.setFieldValue("images", originalImages);
      const originalImageFiles = [...imageFiles];
      setImageFiles(originalImageFiles);
      toast.error("Failed to delete image from server");
    }
  };

  const changeTitleFcn = (title) => {
    formik.setFieldValue("component_name", title);
    debouncedTitleUpdate(title, initialData, handleUpdateTitleAndImages);
  };

  const changeItemNameFcn = (id, name) => {
    debouncedItemNameUpdate(id, name, api);
  };

  const changeSubpartFcn = (id, updatedSubpart) => {
    debouncedSubpartUpdate(id, updatedSubpart, api);
  };

  // Delete item function (not debounced)
  const handleDeleteItemAPI = async (id) => {
    try {
      await api.delete(`/api/rfi-components/item/${id}`);
      toast.success("Item deleted successfully");
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item from server");
    }
  };

  const handleDeleteSubpartAPI = async (id) => {
    try {
      await api.delete(`/api/rfi-components/subpart/${id}`);
      toast.success("Subpart deleted successfully");
    } catch (error) {
      console.error("Error deleting subpart:", error);
      toast.error("Failed to delete subpart from server");
    }
  };

  const checkNewItems =
    formik?.values?.items?.filter((item) => !item.item_id)?.length > 0;
  const oldItems = formik?.values?.items?.filter((item) => item.item_id);

  const checkNewSubparts =
    oldItems?.length > 0 &&
    oldItems?.some((item) =>
      item?.sub_parts?.some((subpart) => !subpart.subpart_id)
    );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedTitleUpdate.cancel();
      debouncedItemNameUpdate.cancel();
    };
  }, []);

  return {
    formik,
    imageFiles,
    isSubmitting,
    handleImageUpload,
    removeImage,
    addItem,
    removeItem,
    addSubPart,
    removeSubPart,
    handleClose,
    resetForm,
    handleModalChange,
    models,
    changeTitleFcn,
    changeItemNameFcn,
    changeSubpartFcn,
    handleDeleteItemAPI,
    handleDeleteSubpartAPI,
    checkNewItems,
    checkNewSubparts,
  };
}
